import { useState, FormEvent } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { ItemType, MaterialGroup } from '../../types';
import { createStockItem, CreateStockItemRequest } from '../../services/storage';
import { Package, Ruler, Weight, MapPin, DollarSign, Tag, Barcode } from 'lucide-react';
import { useToast } from '../../contexts/ToastContext';

const ITEM_TYPES: { value: ItemType; label: string }[] = [
  { value: 'MATERIA_PRIMA', label: 'Matéria-prima' },
  { value: 'FERRAMENTA', label: 'Ferramenta' },
  { value: 'PRODUTO_ACABADO', label: 'Produto Acabado' },
  { value: 'RETALHO', label: 'Retalho' },
  { value: 'EMBALAGEM', label: 'Embalagem' },
];

interface CreateItemDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  accessToken: string;
  groups: MaterialGroup[];
  onSuccess: () => void;
}

export function CreateItemDialog({
  open,
  onOpenChange,
  accessToken,
  groups,
  onSuccess,
}: CreateItemDialogProps) {
  const toast = useToast();
  const [saving, setSaving] = useState(false);

  // Form fields
  const [name, setName] = useState('');
  const [itemType, setItemType] = useState<ItemType>('MATERIA_PRIMA');
  const [skuCode, setSkuCode] = useState('');
  const [width, setWidth] = useState('');
  const [height, setHeight] = useState('');
  const [thickness, setThickness] = useState('');
  const [weightKg, setWeightKg] = useState('');
  const [quantity, setQuantity] = useState('1');
  const [minStock, setMinStock] = useState('');
  const [unit, setUnit] = useState('un');
  const [location, setLocation] = useState('');
  const [costPerUnit, setCostPerUnit] = useState('');
  const [batchNumber, setBatchNumber] = useState('');
  const [purchaseDate, setPurchaseDate] = useState('');
  const [expirationDate, setExpirationDate] = useState('');
  const [barcode, setBarcode] = useState('');
  const [tags, setTags] = useState('');

  const resetForm = () => {
    setName('');
    setItemType('MATERIA_PRIMA');
    setSkuCode('');
    setWidth('');
    setHeight('');
    setThickness('');
    setWeightKg('');
    setQuantity('1');
    setMinStock('');
    setUnit('un');
    setLocation('');
    setCostPerUnit('');
    setBatchNumber('');
    setPurchaseDate('');
    setExpirationDate('');
    setBarcode('');
    setTags('');
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setSaving(true);

    try {
      const request: CreateStockItemRequest = {
        name: name.trim(),
        itemType,
        skuCode: skuCode.trim() || undefined,
        width: width ? parseFloat(width) : undefined,
        height: height ? parseFloat(height) : undefined,
        thickness: thickness ? parseFloat(thickness) : undefined,
        weightKg: weightKg ? parseFloat(weightKg) : undefined,
        quantity: quantity ? parseInt(quantity) : 1,
        minStock: minStock ? parseInt(minStock) : undefined,
        unit: unit.trim() || 'un',
        location: location.trim() || undefined,
        costPerUnit: costPerUnit ? parseFloat(costPerUnit) : undefined,
        batchNumber: batchNumber.trim() || undefined,
        purchaseDate: purchaseDate || undefined,
        expirationDate: expirationDate || undefined,
        barcodes: barcode.trim() ? [barcode.trim()] : undefined,
        tags: tags.trim() ? tags.split(',').map(t => t.trim()).filter(Boolean) : undefined,
      };

      await createStockItem(accessToken, request);
      toast.success('Item criado com sucesso!');
      resetForm();
      onOpenChange(false);
      onSuccess();
    } catch (error) {
      toast.error((error as Error).message);
    } finally {
      setSaving(false);
    }
  };

  const handleClose = (isOpen: boolean) => {
    if (!isOpen) {
      resetForm();
    }
    onOpenChange(isOpen);
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Package className="h-5 w-5" />
            Novo Item de Estoque
          </DialogTitle>
          <DialogDescription>
            Cadastre um novo item no sistema de estoque.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Basic Info */}
          <div className="space-y-4">
            <h3 className="font-medium text-sm text-muted-foreground">Informações Básicas</h3>

            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-2 sm:col-span-2">
                <Label htmlFor="name">Nome do Item *</Label>
                <Input
                  id="name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Ex: Chapa de Acrílico Cristal"
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="itemType">Tipo *</Label>
                <Select value={itemType} onValueChange={(v) => setItemType(v as ItemType)}>
                  <SelectTrigger id="itemType">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {ITEM_TYPES.map((type) => (
                      <SelectItem key={type.value} value={type.value}>
                        {type.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="skuCode">Código SKU</Label>
                <Input
                  id="skuCode"
                  value={skuCode}
                  onChange={(e) => setSkuCode(e.target.value)}
                  placeholder="Ex: MP-ACR-CHP-6MM-CR"
                />
                <p className="text-xs text-muted-foreground">
                  Deixe em branco para gerar automaticamente
                </p>
              </div>
            </div>
          </div>

          {/* Dimensions */}
          <div className="space-y-4">
            <h3 className="font-medium text-sm text-muted-foreground flex items-center gap-2">
              <Ruler className="h-4 w-4" />
              Dimensões (mm)
            </h3>

            <div className="grid gap-4 sm:grid-cols-4">
              <div className="space-y-2">
                <Label htmlFor="width">Largura</Label>
                <Input
                  id="width"
                  type="number"
                  step="0.01"
                  min="0"
                  value={width}
                  onChange={(e) => setWidth(e.target.value)}
                  placeholder="0"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="height">Altura</Label>
                <Input
                  id="height"
                  type="number"
                  step="0.01"
                  min="0"
                  value={height}
                  onChange={(e) => setHeight(e.target.value)}
                  placeholder="0"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="thickness">Espessura</Label>
                <Input
                  id="thickness"
                  type="number"
                  step="0.01"
                  min="0"
                  value={thickness}
                  onChange={(e) => setThickness(e.target.value)}
                  placeholder="0"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="weightKg" className="flex items-center gap-1">
                  <Weight className="h-3 w-3" />
                  Peso (kg)
                </Label>
                <Input
                  id="weightKg"
                  type="number"
                  step="0.001"
                  min="0"
                  value={weightKg}
                  onChange={(e) => setWeightKg(e.target.value)}
                  placeholder="0"
                />
              </div>
            </div>
          </div>

          {/* Quantity & Stock */}
          <div className="space-y-4">
            <h3 className="font-medium text-sm text-muted-foreground">Quantidade e Estoque</h3>

            <div className="grid gap-4 sm:grid-cols-4">
              <div className="space-y-2">
                <Label htmlFor="quantity">Quantidade *</Label>
                <Input
                  id="quantity"
                  type="number"
                  min="0"
                  value={quantity}
                  onChange={(e) => setQuantity(e.target.value)}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="unit">Unidade</Label>
                <Input
                  id="unit"
                  value={unit}
                  onChange={(e) => setUnit(e.target.value)}
                  placeholder="un"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="minStock">Estoque Mínimo</Label>
                <Input
                  id="minStock"
                  type="number"
                  min="0"
                  value={minStock}
                  onChange={(e) => setMinStock(e.target.value)}
                  placeholder="0"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="costPerUnit" className="flex items-center gap-1">
                  <DollarSign className="h-3 w-3" />
                  Custo Unitário
                </Label>
                <Input
                  id="costPerUnit"
                  type="number"
                  step="0.01"
                  min="0"
                  value={costPerUnit}
                  onChange={(e) => setCostPerUnit(e.target.value)}
                  placeholder="0.00"
                />
              </div>
            </div>
          </div>

          {/* Location & Tracking */}
          <div className="space-y-4">
            <h3 className="font-medium text-sm text-muted-foreground flex items-center gap-2">
              <MapPin className="h-4 w-4" />
              Localização e Rastreio
            </h3>

            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="location">Localização</Label>
                <Input
                  id="location"
                  value={location}
                  onChange={(e) => setLocation(e.target.value)}
                  placeholder="Ex: Prateleira A-01"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="batchNumber">Número do Lote</Label>
                <Input
                  id="batchNumber"
                  value={batchNumber}
                  onChange={(e) => setBatchNumber(e.target.value)}
                  placeholder="Ex: LOTE-2026-001"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="purchaseDate">Data de Compra</Label>
                <Input
                  id="purchaseDate"
                  type="date"
                  value={purchaseDate}
                  onChange={(e) => setPurchaseDate(e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="expirationDate">Data de Validade</Label>
                <Input
                  id="expirationDate"
                  type="date"
                  value={expirationDate}
                  onChange={(e) => setExpirationDate(e.target.value)}
                />
              </div>
            </div>
          </div>

          {/* Barcode & Tags */}
          <div className="space-y-4">
            <h3 className="font-medium text-sm text-muted-foreground flex items-center gap-2">
              <Barcode className="h-4 w-4" />
              Código de Barras e Tags
            </h3>

            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="barcode">Código de Barras Externo</Label>
                <Input
                  id="barcode"
                  value={barcode}
                  onChange={(e) => setBarcode(e.target.value)}
                  placeholder="Escaneie ou digite o código"
                />
                <p className="text-xs text-muted-foreground">
                  Código do fornecedor ou existente
                </p>
              </div>
              <div className="space-y-2">
                <Label htmlFor="tags" className="flex items-center gap-1">
                  <Tag className="h-3 w-3" />
                  Tags
                </Label>
                <Input
                  id="tags"
                  value={tags}
                  onChange={(e) => setTags(e.target.value)}
                  placeholder="Ex: urgente, cliente-x, projeto-y"
                />
                <p className="text-xs text-muted-foreground">
                  Separe as tags por vírgula
                </p>
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => handleClose(false)}
              disabled={saving}
            >
              Cancelar
            </Button>
            <Button type="submit" disabled={saving}>
              {saving ? 'Salvando...' : 'Criar Item'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
